import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import {
  FileText, Plus, Trash2, GripVertical, Send, Sparkles,
  CheckCircle2, AlertTriangle,
} from "lucide-react";

/**
 * Module 3 — Drag-and-drop Resolution Sandbox.
 *
 * Two columns:
 *   • Left: clause library (preambulatory + operative). Drag a word into
 *     the draft to start a clause.
 *   • Right: the draft skeleton — title, sponsors, preambulatory list,
 *     operative list. Each clause is a row: opening word (from library)
 *     + the user-typed body of the clause.
 *
 * On submit, the backend rubric grades structure (NOT content) and awards
 * 25 XP once per day if the score is ≥ 60.
 */
export default function ResolutionSandboxPage() {
  const [catalogue, setCatalogue] = useState({ preambulatory: [], operative: [] });
  const [title, setTitle] = useState("");
  const [sponsors, setSponsors] = useState("");
  const [preambulatory, setPreambulatory] = useState([]);
  const [operative, setOperative] = useState([]);
  const [grade, setGrade] = useState(null);
  const [dragging, setDragging] = useState(null);  // { kind, opening }

  useEffect(() => {
    api.get("/lms/resolution/clauses").then((r) => setCatalogue(r.data));
  }, []);

  // Drag handlers — both for the library and for re-ordering within a list
  const onDragStart = (kind, opening) => (e) => {
    setDragging({ kind, opening });
    e.dataTransfer.setData("text/plain", `${kind}:${opening}`);
  };
  const onDropToList = (kind) => (e) => {
    e.preventDefault();
    const [k, opening] = (e.dataTransfer.getData("text/plain") || "").split(":");
    if (k !== kind || !opening) return;
    const row = { id: cryptoId(), opening, body: "" };
    if (kind === "preambulatory") setPreambulatory((p) => [...p, row]);
    else setOperative((p) => [...p, row]);
    setDragging(null);
  };
  const allowDrop = (e) => e.preventDefault();

  const removeRow = (kind, id) => {
    if (kind === "preambulatory") setPreambulatory((p) => p.filter((r) => r.id !== id));
    else setOperative((p) => p.filter((r) => r.id !== id));
  };
  const updateRow = (kind, id, body) => {
    const next = (rows) => rows.map((r) => r.id === id ? { ...r, body } : r);
    if (kind === "preambulatory") setPreambulatory(next);
    else setOperative(next);
  };

  const submit = async () => {
    setGrade({ loading: true });
    try {
      const r = await api.post("/lms/resolution/score", {
        title, sponsors: sponsors.split(",").map((s) => s.trim()).filter(Boolean),
        preambulatory: preambulatory.map((p) => ({ opening: p.opening, body: p.body })),
        operative: operative.map((p) => ({ opening: p.opening, body: p.body })),
      });
      setGrade(r.data);
    } catch (e) {
      setGrade({ error: e.response?.data?.detail || "Could not score" });
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="resolution-sandbox-root">
        <header className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
            <FileText className="h-3 w-3" /> Resolution Sandbox
          </p>
          <h1 className="mt-1 text-2xl font-bold text-[#0A192F]">
            Practice writing a resolution
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Drag opening words from the library into your draft. We grade
            structure (not content) and award XP once per day for a healthy draft.
          </p>
        </header>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Library */}
          <aside className="space-y-4" data-testid="sandbox-library">
            <ClauseBank
              title="Preambulatory" tone="gold"
              words={catalogue.preambulatory}
              onDragStart={(w) => onDragStart("preambulatory", w)}
            />
            <ClauseBank
              title="Operative" tone="blue"
              words={catalogue.operative}
              onDragStart={(w) => onDragStart("operative", w)}
            />
          </aside>

          {/* Draft */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5"
                   data-testid="sandbox-draft">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              Title
            </label>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="A resolution to…"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              data-testid="sandbox-title"
            />
            <label className="mt-3 block text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              Sponsors (comma-separated)
            </label>
            <input
              type="text" value={sponsors} onChange={(e) => setSponsors(e.target.value)}
              placeholder="France, Brazil, Kenya"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              data-testid="sandbox-sponsors"
            />

            <ClauseList
              title="Preambulatory clauses" kind="preambulatory" tone="gold"
              rows={preambulatory} drop={onDropToList("preambulatory")}
              allowDrop={allowDrop}
              onRemove={(id) => removeRow("preambulatory", id)}
              onUpdate={(id, body) => updateRow("preambulatory", id, body)}
              suffix=","
            />
            <ClauseList
              title="Operative clauses" kind="operative" tone="blue"
              rows={operative} drop={onDropToList("operative")}
              allowDrop={allowDrop}
              onRemove={(id) => removeRow("operative", id)}
              onUpdate={(id, body) => updateRow("operative", id, body)}
              suffix=";" numbered
            />

            <button
              type="button" onClick={submit}
              className="mt-5 px-4 py-2 rounded-md bg-[#0A192F] text-white text-sm font-semibold flex items-center gap-1.5"
              data-testid="sandbox-grade-btn"
            >
              <Send className="h-3.5 w-3.5" /> Grade my draft
            </button>

            {grade && !grade.loading && <GradeCard grade={grade} />}
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
}

function ClauseBank({ title, tone, words, onDragStart }) {
  const accent = tone === "gold" ? "border-[#D4AF37] bg-[#FFFBEB]" : "border-[#0F4C81] bg-blue-50";
  return (
    <div className={`rounded-2xl border-2 ${accent} p-4`} data-testid={`bank-${tone}`}>
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-700">{title}</p>
      <p className="mt-1 text-[11px] text-slate-500">Drag a word into the draft →</p>
      <div className="mt-3 flex flex-wrap gap-1.5 max-h-[260px] overflow-y-auto">
        {words.map((w) => (
          <span
            key={w} draggable
            onDragStart={onDragStart(w)}
            className="cursor-grab px-2 py-1 rounded-md bg-white border border-slate-300 text-[11px] font-medium text-[#0A192F] hover:border-[#D4AF37]"
            data-testid={`clause-word-${tone}-${w}`}
          >
            {w}
          </span>
        ))}
      </div>
    </div>
  );
}

function ClauseList({ title, kind, tone, rows, drop, allowDrop, onRemove, onUpdate, suffix, numbered }) {
  const accent = tone === "gold" ? "border-[#D4AF37]" : "border-[#0F4C81]";
  return (
    <div className="mt-5">
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{title}</p>
      <div
        onDrop={drop} onDragOver={allowDrop}
        className={`mt-2 rounded-xl border-2 border-dashed ${accent} p-3 min-h-[80px] bg-slate-50`}
        data-testid={`droplist-${kind}`}
      >
        {rows.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">
            Drop {title.toLowerCase()} here.
          </p>
        )}
        <ol className="space-y-2 list-none">
          {rows.map((row, i) => (
            <li key={row.id} className="flex items-start gap-2"
                data-testid={`clause-row-${kind}-${i}`}>
              <GripVertical className="h-4 w-4 text-slate-400 mt-2" />
              {numbered && <span className="font-mono text-xs text-slate-500 mt-2">{i + 1}.</span>}
              <span className="italic font-semibold text-[#0A192F] text-sm mt-1.5">
                {row.opening}
              </span>
              <input
                type="text" value={row.body}
                onChange={(e) => onUpdate(row.id, e.target.value)}
                placeholder="…continue the clause…"
                className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
                data-testid={`clause-body-${kind}-${i}`}
              />
              <span className="text-slate-500 text-sm mt-1.5">{suffix}</span>
              <button type="button" onClick={() => onRemove(row.id)}
                      className="p-1 text-rose-500 hover:text-rose-700"
                      data-testid={`clause-remove-${kind}-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function GradeCard({ grade }) {
  if (grade.error) {
    return (
      <p className="mt-4 text-sm text-rose-700" data-testid="grade-error">
        {grade.error}
      </p>
    );
  }
  const cls = grade.band === "ready" ? "border-emerald-400 bg-emerald-50"
            : grade.band === "almost" ? "border-amber-400 bg-amber-50"
            : "border-rose-300 bg-rose-50";
  return (
    <div className={`mt-5 rounded-xl border-2 ${cls} p-4`} data-testid="grade-card">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-[#0A192F]">
          Score: {grade.score} / {grade.max_score}
        </h3>
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
          {grade.band}
        </span>
      </div>
      {grade.xp_awarded > 0 && (
        <p className="mt-2 text-sm text-[#0F4C81] font-semibold flex items-center gap-1"
           data-testid="grade-xp">
          <Sparkles className="h-4 w-4" /> +{grade.xp_awarded} XP awarded
        </p>
      )}
      <ul className="mt-3 space-y-1.5">
        {grade.findings.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs"
              data-testid={`finding-${i}`}>
            {f.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5" />
                   : <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />}
            <span className={f.ok ? "text-slate-700" : "text-amber-800 font-medium"}>
              {f.label}{!f.ok && f.hint && ` — ${f.hint}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function cryptoId() {
  // Use the browser crypto API where available; fall back to Math.random for
  // older browsers (this is purely a client-side row key, never persisted).
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "row-" + Math.random().toString(36).slice(2, 10);
}
